<template>
  <div>
    <el-row class="page-buttons">
      <editor-button v-if="allowActions && allowCreate && hasCreatePermission" action="add" @click="handleCreate" />
      <editor-button v-if="!isEmbedded" action="cancel" style="float:right" @click="close" />
    </el-row>
    <pagination
      v-if="usePagination"
      v-show="numberOfItems > 0"
      :total="numberOfItems"
      :page.sync="listQuery.page"
      :limit.sync="listQuery.limit"
      @pagination="refreshList"
    />
    <el-table
      v-loading="loading"
      :data="items"
      border
      fit
      highlight-current-row
      style="width: 100%"
      @row-dblclick="rowDblClick"
      @sort-change="sortChange"
    >
      <div v-for="item in items" :key="item.id">
        {{ item }}
      </div>
      <el-table-column v-for="column in columns" :key="column.field || column" label-class-name="filter-cell">
        <template slot="header" slot-scope="{}">
          <el-input
            v-if="column.searchable === undefined ? true : column.searchable"
            v-model="search[column.field || column]"
            size="mini"
            :placeholder="$t('table.searchPlaceholder')"
            clearable
            prefix-icon="el-icon-search"
          />
        </template>
        <el-table-column
          :prop="column.field || column"
          :sortable="column.sortable === undefined ? true : column.sortable"
          :label="$t(column.label || 'common.' + (column.field || column))"
          :class-name="column.class"
        >
          <template slot-scope="scope">
            <span v-if="!column.type">{{ scope.row[column.field || column] }}</span>
            <span v-if="column.type === 'dateTime'">
              <date-time-picker v-model="scope.row[column.field]" :format="column.format || dateTimeFormat" disabled />
            </span>
            <span v-if="column.type === 'currency'">{{
              '€ ' + scope.row[column.field].toFixed(2).replace('.', ',')
            }}</span>
          </template>
        </el-table-column>
      </el-table-column>
      <el-table-column v-if="allowActions" align="center" :label="$t('table.actions')">
        <template slot-scope="scope">
          <el-button type="primary" icon="el-icon-edit" circle @click="handleEdit(scope.row.id)" />
          <el-button
            v-if="allowDelete && hasDeletePermission"
            type="danger"
            icon="el-icon-delete"
            circle
            @click="deleteConfirm(scope.row)"
          />
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import EditorButton from './EditorButton';
import DateTimePicker from '@/components/DSE/DateTimePicker';
import { hasCreatePermissionForCode } from '@/utils/permission';
import { entityToStore } from '@/utils/store';
import { closeCurrentTag } from '@/utils/tags';
const debounce = require('lodash.debounce');

export default {
  name: 'CrudTable',
  components: {
    Pagination,
    EditorButton,
    DateTimePicker
  },
  props: {
    allowActions: {
      default: true,
      type: Boolean
    },
    allowCreate: {
      default: true,
      type: Boolean
    },
    allowDelete: {
      default: true,
      type: Boolean
    },
    columns: {
      /**
       * Array of Objects, Strings
       * Object keys: field: String, label: String, type: String, sortable: Boolean
       *  */
      default() {
        return {};
      },
      type: Array
    },
    entity: {
      default: '',
      type: String
    },
    onRowDblClick: {
      type: Function
    },
    onCreate: {
      type: Function
    },
    onEdit: {
      type: Function
    },
    requiresDomainId: {
      default: false,
      type: Boolean
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    generateSummary: {
      type: Function,
      default: ({ id, name, code, description, reference }) => {
        if (name === undefined) {
          let r = `${code || id}`;
          if (description) {
            r += ` (${description})`;
          }
          return r;
        }
        return `${name} (${reference || code || description})`;
      }
    },
    itemFilter: {
      type: Function,
      default: () => true
    },
    usePagination: {
      type: Boolean,
      default: false
    },
    trashedID: {
      type: Number,
      default: 0
    },
    isEmbedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      entityStoreName: entityToStore(this.entity),
      listQuery: {
        limit: 20,
        page: 1
      },
      search: {},
      sort: null,
      total: 0
    };
  },
  computed: {
    dateTimeFormat() {
      return this.$store.getters.dateTimeFormat;
    },
    hasCreatePermission() {
      return hasCreatePermissionForCode(this.entity, this.$store.getters.permissions);
    },
    hasDeletePermission() {
      return this.hasCreatePermission;
    },
    items() {
      return this.$store.getters[this.entityStoreName + '/' + this.storeRetrieveItemsGetter].filter(item =>
        this.itemFilter(item, this)
      );
    },
    listParams() {
      const params = this.requiresDomainId ? { domainID: this.$store.getters.domainID } : {};
      params.query = { ...this.filters };
      if (this.usePagination) {
        params.query = { ...params.query, ...this.listQuery };
      }
      if (this.trashedID > 0) {
        params.query.withTrashedID = this.trashedID;
      }
      if (this.sort) {
        params.query.sort = this.sort;
      }
      if (Object.keys(this.search).length > 0) {
        params.query = { ...params.query, ...this.search };
      }
      return params;
    },
    loading() {
      return this.$store.getters[this.entityStoreName + '/isLoading'];
    },
    numberOfItems() {
      return this.$store.getters[this.entityStoreName + '/totalItemsCount'];
    },
    storeFetchListAction() {
      if (this.usePagination) {
        return 'replacePaginatedItems';
      }
      return 'replaceItems';
    },
    storeRetrieveItemsGetter() {
      if (this.usePagination) {
        return 'loadedPaginatedItems';
      }
      return 'loadedItems';
    }
  },
  watch: {
    search: {
      deep: true,
      handler: function(value) {
        this.listQuery.page = 1;
        this.debouncedUpdateSearch();
      }
    }
  },
  created() {
    this.refreshList();
    this.$store.dispatch('getUserInfo', { withPermissions: true });
    this.debouncedUpdateSearch = debounce(this.refreshList, 500);
  },
  methods: {
    deleteConfirm(row) {
      const { id } = row;
      const summary = this.generateSummary(row);
      this.$store
        .dispatch('notify/deleteConfirm', summary)
        .then(() => {
          this.$store
            .dispatch(this.entityStoreName + '/deleteItem', {
              id: id
            })
            .then(() => {
              this.$store.dispatch('notify/deleteCompleted');
            });
        })
        .catch(() => {
          this.$store.dispatch('notify/deleteCancelled');
        });
    },
    handleCreate() {
      this.onCreate ? this.onCreate() : this.$router.push({ name: 'Create' + this.entity });
      this.$emit('create');
    },
    handleEdit(id) {
      this.onEdit ? this.onEdit() : this.$router.push({ name: 'Edit' + this.entity, params: { id } });
      this.$emit('edit', id);
    },
    routerPushFromRow(row) {
      this.$router.push({
        name: 'Edit' + this.entity,
        params: { id: row.id }
      });
    },
    rowDblClick(row) {
      if (this.onRowDblClick) {
        this.onRowDblClick(row);
      } else {
        this.routerPushFromRow(row);
      }
      this.$emit('input', row);
    },
    sortChange({ column, prop, order }) {
      if (prop !== null) {
        this.sort = order === 'descending' ? prop + '_desc' : prop;
      }
      this.$store.dispatch(this.entityStoreName + '/' + this.storeFetchListAction, this.listParams);
    },
    refreshList() {
      this.$store.dispatch(this.entityStoreName + '/' + this.storeFetchListAction, this.listParams);
    },
    close() {
      closeCurrentTag(this);
    }
  }
};
</script>

<style scoped>
.edit-input {
  padding-right: 100px;
}

.cancel-btn {
  position: absolute;
  right: 15px;
  top: 10px;
}
</style>
<style lang="scss">
.filter-cell > div {
  padding-left: 0px !important;
  padding-right: 0px !important;
}

.hide-overflow {
  .cell {
    white-space: nowrap;
  }
}
</style>
